import React, { useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

// A small, dependency-free emoji set — no external picker library needed.
const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😊', '😉', '😍', '😘', '😗', '😜',
  '🤗', '🤔', '🙄', '😴', '😢', '😭', '😡', '🥰', '😎', '🤩',
  '😇', '🙂', '😏', '😅', '🥲', '😳', '🤤', '😱', '🥺', '😤',
  '👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '👋', '✌️', '🤞',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕',
  '🔥', '✨', '⭐', '💯', '🎉', '🎊', '🎁', '🌹', '🍾', '☕',
  '🧘', '🧴', '💧', '♨️', '🌿', '🌸', '🏞️', '🌄', '🌙', '🔆',
];

interface EmojiPickerButtonProps {
  onSelect: (emoji: string) => void;
  disabled?: boolean;
}

/**
 * Ported from Authentication76's EmojiPickerButton — a reusable emoji
 * "keyboard": an icon button that opens a popover grid of emojis.
 * Deliberately generic so it can be dropped next to any text input/textarea.
 */
const EmojiPickerButton: React.FC<EmojiPickerButtonProps> = ({ onSelect, disabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handlePick = (emoji: string) => {
    onSelect(emoji);
    setOpen(false);
  };

  const content = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(10, 1fr)',
        gap: 4,
        maxWidth: 280,
      }}
    >
      {EMOJIS.map((emoji) => (
        <Button
          key={emoji}
          type="text"
          size="small"
          style={{ fontSize: 18, padding: 0, width: 26, height: 26 }}
          onClick={() => handlePick(emoji)}
        >
          {emoji}
        </Button>
      ))}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} content={content} trigger="click" placement="topRight">
      <Tooltip title={translate(t, TRANSLATIONS.EMOJI_PICKER_TOOLTIP)}>
        <Button type="text" size="small" icon={<SmileOutlined />} disabled={disabled} />
      </Tooltip>
    </Popover>
  );
};

export default EmojiPickerButton;
